import React from 'react';
import styled from 'libs/styled';
import Text from 'components/Text';
import Button from 'components/Button';
import Link from '@fogia/core-web/components/Link';
import SanityBackgroundMedia, {
    type MediaItemComponent,
} from '@fogia/core-web/components/SanityMedia/SanityBackgroundMedia';
import { getLayoutImageSizes } from '@fogia/core-web/utils/getLayoutImageSizes';
import type { HighlightedContentItemTransformed } from './types';

const ItemWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const MediaWrapper = styled('div')`
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 5;
    overflow: hidden;
    background-color: var(--opacity-primary-30);
`;

const MediaInner = styled('div')`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;

const TitleAndButtonWrapper = styled('div')`
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export interface HighlightedContentItemProps {
    item: HighlightedContentItemTransformed;
}

const HighlightedContentItem: React.FC<HighlightedContentItemProps> = ({ item }) => {
    const { title, href, target, label, portrait, alt } = item;
    const accessibleName = title && label ? `${title}, ${label}` : label || title || '';

    const content = (
        <>
            <MediaWrapper>
                <MediaInner>
                    <SanityBackgroundMedia
                        media={portrait as MediaItemComponent}
                        alt={alt ?? title ?? ''}
                        objectFit="cover"
                        loading="eager"
                        sizes={getLayoutImageSizes('sliderWidth')}
                    />
                </MediaInner>
            </MediaWrapper>
            {(title || label) && (
                <TitleAndButtonWrapper>
                    {title && (
                        <Text $as="span" typography="Primary/15_120_510_-1">
                            {title}
                        </Text>
                    )}
                    {label && (
                        <Button as={href ? 'span' : undefined} theme="textButtonUnderline" size="small">
                            {label}
                        </Button>
                    )}
                </TitleAndButtonWrapper>
            )}
        </>
    );

    if (href) {
        return (
            <ItemWrapper as={Link} to={href} target={target} aria-label={accessibleName || undefined}>
                {content}
            </ItemWrapper>
        );
    }

    return <ItemWrapper>{content}</ItemWrapper>;
};

export default HighlightedContentItem;
